import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Row, Col, Card, CardBody, CardHeader, Input, Label
} from "reactstrap";
import {
  Table, Thead, Tbody, Tr, Th, Td
} from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { MetaTags } from "react-meta-tags";
import { withTranslation } from "react-i18next";
import CustomPagination from "components/Common/CustomPagination";
import TableLoader from "components/Common/TableLoader";
import { ReminderStatusOptions } from "components/Common/ReminderStatusOptions";
import formatDate from "helpers/formatDate";
import { fetchRemindersStart, editReminderStart } from "store/reminders/actions";
import { fetchDictionaryStart } from "store/dictionary/actions";

function RemindersList(props) {
  const dispatch = useDispatch();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [callStatusFilter, setCallStatusFilter] = useState("");
  const [sizePerPage, setSizePerPage] = useState(10);

  const {
    reminders = [],
    loading,
    totalDocs = 0,
    totalPages = 1,
    page = 1,
    hasNextPage = false,
    hasPrevPage = false,
    nextPage = null,
    prevPage = null,
  } = useSelector((state) => state.remindersReducer || {});

  const { callStatus: csOptions } = useSelector((state) => state.dictionaryReducer || {});

  const callStatusOptions = React.useMemo(() => {
    if (!csOptions) return [];
    return Array.isArray(csOptions) ? csOptions : Object.keys(csOptions);
  }, [csOptions]);

  const loadData = (pageNumber = 1, pageLimit = sizePerPage) => {
    const params = {
      page: pageNumber,
      limit: parseInt(pageLimit),
    };

    if (fromDate && toDate) {
      params.start = new Date(`${fromDate}T00:00:00.000`).toISOString();
      params.end = new Date(`${toDate}T23:59:59.999`).toISOString();
    } else if (fromDate) {
      params.start = new Date(`${fromDate}T00:00:00.000`).toISOString();
    } else if (toDate) {
      params.end = new Date(`${toDate}T23:59:59.999`).toISOString();
    }

    if (callStatusFilter) {
      params.callStatus = callStatusFilter;
    }

    dispatch(fetchRemindersStart(params));
  };

  useEffect(() => {
    dispatch(fetchDictionaryStart());
  }, [dispatch]);

  useEffect(() => {
    loadData(1);
  }, [fromDate, toDate, callStatusFilter, sizePerPage]);

  const handleClearFilters = () => {
    setFromDate("");
    setToDate("");
    setCallStatusFilter("");
  };

  const handleStatusChange = (reminderId, newStatus) => {
    dispatch(
      editReminderStart({
        id: reminderId,
        callStatus: newStatus,
      })
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{props.t("Reminders")} | EM Markets</title>
        </MetaTags>
        <div className="container-fluid">
          <Row>
            <Col className="col-12">
              <Card>
                <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <h4 className="card-title mb-0 color-primary">{props.t("Reminders")}</h4>
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    {/* Date Range Filter */}
                    <div className="d-flex align-items-center gap-2">
                      <Label className="mb-0 fw-bold">{props.t("From")}:</Label>
                      <Input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="form-control form-control-sm"
                        style={{ width: "150px" }}
                      />
                      <Label className="mb-0 fw-bold me-1 ms-1">{props.t("To")}:</Label>
                      <Input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="form-control form-control-sm"
                        style={{ width: "150px" }}
                      />
                    </div>

                    {/* Call Status Filter */}
                    <div className="d-flex align-items-center gap-2">
                      <Label className="mb-0 fw-bold">{props.t("Call Status")}:</Label>
                      <Input
                        type="select"
                        value={callStatusFilter}
                        onChange={(e) => setCallStatusFilter(e.target.value)}
                        className="form-select form-select-sm text-capitalize"
                        style={{ width: "170px" }}
                      >
                        <option value="">{props.t("All Call Statuses")}</option>
                        <ReminderStatusOptions options={callStatusOptions} value={callStatusFilter} />
                      </Input>
                    </div>

                    {/* Clear Filters Button */}
                    {(fromDate || toDate || callStatusFilter) && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleClearFilters}
                      >
                        {props.t("Clear Filters")}
                      </button>
                    )}
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="table-rep-plugin">
                    <div className="table-responsive mb-0" data-pattern="priority-columns">
                      <Table id="tech-companies-1" className="table table-hover align-middle">
                        <Thead className="text-center table-light">
                          <Tr>
                            <Th><span className="color-primary">{props.t("Lead Name")}</span></Th>
                            <Th><span className="color-primary">{props.t("Reminder Note")}</span></Th>
                            <Th><span className="color-primary">{props.t("Next Call Date & Time")}</span></Th>
                            <Th><span className="color-primary">{props.t("Call Status")}</span></Th>
                            <Th><span className="color-primary">{props.t("Date Created")}</span></Th>
                            <Th><span className="color-primary">{props.t("Created By")}</span></Th>
                          </Tr>
                        </Thead>
                        <Tbody className="text-center">
                          {loading && <TableLoader colSpan={6} />}
                          {!loading && reminders.length === 0 && (
                            <Tr>
                              <Td colSpan={6} className="text-center">
                                {props.t("No records found")}
                              </Td>
                            </Tr>
                          )}
                          {!loading &&
                            reminders.map((item, index) => {
                              const customer = item.customerId || {};
                              const leadName =
                                customer.firstName || customer.lastName
                                  ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim()
                                  : customer.recordId
                                    ? `Lead #${customer.recordId}`
                                    : "N/A";
                              const createdBy = item.createdBy
                                ? `${item.createdBy.firstName || ""} ${item.createdBy.lastName || ""}`.trim()
                                : "";

                              return (
                                <Tr key={item._id || index}>
                                  <Td>
                                    {customer._id ? (
                                      <Link
                                        to={{
                                          pathname: `/leads/${customer._id}/profile`,
                                          state: { clientId: customer._id },
                                        }}
                                        className="text-primary fw-bold"
                                      >
                                        <strong className="text-capitalize">{leadName}</strong>
                                      </Link>
                                    ) : (
                                      <strong className="text-capitalize">{leadName}</strong>
                                    )}
                                  </Td>
                                  <Td style={{
                                    maxWidth: "250px",
                                    whiteSpace: "normal" 
                                  }}>
                                    {item.note}
                                  </Td>
                                  <Td>{item.nextCallDate ? formatDate(item.nextCallDate) : "-"}</Td>
                                  <Td style={{ minWidth: "150px" }}>
                                    <Input
                                      type="select"
                                      value={item.callStatus || "open"}
                                      onChange={(e) => handleStatusChange(item._id, e.target.value)}
                                      className="form-select form-select-sm text-capitalize"
                                    >
                                      <ReminderStatusOptions options={callStatusOptions} value={item.callStatus} />
                                    </Input>
                                  </Td>
                                  <Td>{item.createdAt ? formatDate(item.createdAt) : "-"}</Td>
                                  <Td>{createdBy || "-"}</Td>
                                </Tr>
                              );
                            })}
                        </Tbody>
                      </Table>
                    </div>
                  </div>
                  <CustomPagination
                    totalPages={totalPages}
                    docs={reminders}
                    sizePerPage={parseInt(sizePerPage)}
                    page={page}
                    totalDocs={totalDocs}
                    hasNextPage={hasNextPage}
                    hasPrevPage={hasPrevPage}
                    nextPage={nextPage}
                    prevPage={prevPage}
                    setSizePerPage={(size) => {
                      setSizePerPage(parseInt(size));
                    }}
                    onChange={(pageNumber, size) => loadData(pageNumber, size || sizePerPage)}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
}

export default withTranslation()(RemindersList);
